/**
 * Admin → SpamGuard. Platform-only (the backend gates every route with
 * RequirePlatform, so non-platform tenants get 403; the sidebar hides the
 * links too). Two surfaces:
 *   - Review queue: campaigns SpamGuard held at launch, with approve/block.
 *   - Keyword rules: operator-loadable spam/fraud terms (block/review/weight).
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '@/lib/api';

// ----- shared types ---------------------------------------------------------

/** A structured screening finding. `code` is a stable machine id, `detail`
 *  the human-readable specifics. Mirrors spamguard.Reason. */
export interface SpamReason {
  code: string;
  detail: string;
}

export type SpamChannel = 'sms' | 'voice';
export type SpamVerdict = 'allow' | 'review' | 'block' | 'unscored';

// ----- review queue ---------------------------------------------------------

export interface SpamVerdictRow {
  id: number;
  tenant_id: number;
  tenant_name: string;
  channel: SpamChannel;
  campaign_id: number;
  campaign_name: string;
  /** 0-100 classifier score. Null when the campaign was held by a keyword
   *  rule or the LLM failed (unscored) — never shown to the tenant. */
  score: number | null;
  verdict: SpamVerdict;
  reasons: SpamReason[];
  created_at: string;
  /** Set once an operator decided. On block verdicts, 'approved' means an
   *  override was granted (an unchanged relaunch passes the screener). */
  decision?: SpamDecision | null;
}

interface SpamReviewResponse {
  items: SpamVerdictRow[];
  total: number;
  /** Recent hard blocks — read-only visibility + override-approve. */
  blocked: SpamVerdictRow[];
}

const REVIEW_KEY = ['admin', 'spam-review'] as const;
const KEYWORDS_KEY = ['admin', 'spam-keywords'] as const;

/**
 * The held-campaign review queue. Polls on a slow cadence so a newly held
 * campaign appears without a manual refresh while the operator is on the
 * page, without hammering the LLM-backed endpoint.
 */
export function useSpamReview() {
  return useQuery({
    queryKey: REVIEW_KEY,
    queryFn: () => api.get<SpamReviewResponse>('/v1/admin/spam-review'),
    staleTime: 15_000,
    refetchInterval: 30_000,
  });
}

export type SpamDecision = 'approved' | 'blocked';

/**
 * Stamp a decision on a held verdict. 'approved' clears it from the queue
 * (the operator then re-launches the campaign from its page); 'blocked'
 * records the rejection. Invalidates the queue so the row drops out.
 */
export function useDecideSpamReview() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, decision }: { id: number; decision: SpamDecision }) =>
      api.post<SpamVerdictRow>(`/v1/admin/spam-review/${id}/decide`, { decision }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: REVIEW_KEY });
    },
  });
}

// ----- keyword rules --------------------------------------------------------

export type KeywordMatchType = 'word' | 'substring' | 'regex';
export type KeywordAction = 'block' | 'review' | 'weight';

export interface SpamKeyword {
  id: number;
  term: string;
  match_type: KeywordMatchType;
  action: KeywordAction;
  /** Points added to the LLM score. Only meaningful when action='weight';
   *  null otherwise. */
  weight: number | null;
  enabled: boolean;
  notes?: string;
  created_at: string;
  updated_at: string;
}

interface SpamKeywordsResponse {
  items: SpamKeyword[];
  total: number;
}

export interface KeywordInput {
  term: string;
  match_type: KeywordMatchType;
  action: KeywordAction;
  /** Required (and only used) when action='weight'. */
  weight?: number | null;
  enabled?: boolean;
  notes?: string;
}

export function useSpamKeywords() {
  return useQuery({
    queryKey: KEYWORDS_KEY,
    queryFn: () => api.get<SpamKeywordsResponse>('/v1/admin/spam-keywords'),
    staleTime: 30_000,
  });
}

export function useCreateSpamKeyword() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: KeywordInput) => api.post<SpamKeyword>('/v1/admin/spam-keywords', input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEYWORDS_KEY });
    },
  });
}

export function useUpdateSpamKeyword() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: number; input: KeywordInput }) =>
      api.put<SpamKeyword>(`/v1/admin/spam-keywords/${id}`, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEYWORDS_KEY });
    },
  });
}

export function useDeleteSpamKeyword() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.delete<void>(`/v1/admin/spam-keywords/${id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEYWORDS_KEY });
    },
  });
}
